<template>
    <div class="sa-page">
        <header class="sa-header">
            <div class="sa-header__inner">
                <div class="sa-brand">DG Ventures</div>
                <div v-if="stage === 'assessment'" class="sa-header__save">
                    <v-icon x-small color="white" class="mr-1">{{ saving ? 'mdi-cloud-sync-outline' : 'mdi-cloud-check-outline' }}</v-icon>
                    {{ saving ? 'Saving…' : 'Progress saved' }}
                </div>
            </div>
        </header>

        <main class="sa-main">
            <!-- Loading -->
            <div v-if="stage === 'loading'" class="text-center py-16">
                <v-progress-circular indeterminate color="#3949ab" />
            </div>

            <!-- Could not load -->
            <div v-else-if="stage === 'error'" class="sa-card text-center">
                <v-icon size="48" color="#c62828">mdi-wifi-alert</v-icon>
                <h2 class="sa-title mt-3">We couldn't load the questionnaire</h2>
                <p class="sa-muted">{{ loadError }}</p>
                <v-btn color="#3949ab" dark depressed class="text-none" @click="init">Try again</v-btn>
            </div>

            <!-- Welcome & registration -->
            <div v-else-if="stage === 'welcome'" class="sa-card">
                <div class="sa-eyebrow">Careers at DG Ventures</div>
                <h1 class="sa-hero">Work Style &amp; Personality Questionnaire</h1>
                <p class="sa-lead">
                    Thanks for your interest in joining our team. This questionnaire helps us get to know you as a
                    person: how you think, learn and get on with people.
                </p>

                <div class="sa-points">
                    <div class="sa-point">
                        <v-icon color="#3949ab">mdi-school-outline</v-icon>
                        <div><strong>No experience needed.</strong> Examples from school, college, sport, family,
                            hobbies or any job all count.</div>
                    </div>
                    <div class="sa-point">
                        <v-icon color="#3949ab">mdi-timer-outline</v-icon>
                        <div><strong>About 25 minutes, no time limit.</strong> Your progress saves as you go, so you can
                            come back on this device.</div>
                    </div>
                    <div class="sa-point">
                        <v-icon color="#3949ab">mdi-emoticon-happy-outline</v-icon>
                        <div><strong>Just be yourself.</strong> This is about personality, not right or wrong answers.
                            Answer as you really are, not as you think you should. We check answers for consistency.</div>
                    </div>
                </div>

                <v-form ref="form" v-model="formValid" @submit.prevent="start">
                    <v-text-field v-model.trim="name" label="Full name" outlined :rules="[rules.required]"
                        autocomplete="name" class="mb-1" />
                    <v-text-field v-model.trim="email" label="Email address" type="email" outlined
                        :rules="[rules.required, rules.email]" autocomplete="email" class="mb-1" />

                    <div class="sa-consent" :class="{ 'sa-consent--error': consentError && !consent }">
                        <v-checkbox v-model="consent" hide-details class="mt-0 pt-0" color="#3949ab">
                            <template #label>
                                <span class="sa-consent__label">I understand how my answers are used (below).</span>
                            </template>
                        </v-checkbox>
                        <p class="sa-consent__text">
                            Your answers are used only to assess your application for a role with DG Ventures.
                            They are scored automatically, and your written answers are read by an AI assistant
                            (Claude, by Anthropic) that suggests scores and a summary. A member of our recruitment team
                            reviews every result: no decision is made by AI alone. You can ask us for a copy of your
                            answers, or to delete them, at any time.
                        </p>
                        <div v-if="consentError && !consent" class="sa-error sa-consent__error">
                            Please tick the box to confirm before you start.
                        </div>
                    </div>

                    <v-alert v-if="startError" type="error" dense text class="mt-3 mb-0">{{ startError }}</v-alert>

                    <v-btn type="submit" block x-large depressed dark color="#3949ab" class="text-none mt-5 sa-cta"
                        :loading="starting">
                        Start the questionnaire
                        <v-icon right>mdi-arrow-right</v-icon>
                    </v-btn>
                </v-form>
            </div>

            <!-- Assessment -->
            <div v-else-if="stage === 'assessment' && page">
                <div class="sa-progress">
                    <div class="sa-progress__row">
                        <span class="sa-progress__section">{{ page.sectionLabel }}</span>
                        <span class="sa-progress__count">Page {{ step + 1 }} of {{ pages.length }}</span>
                    </div>
                    <v-progress-linear :value="progress" height="8" rounded color="#3949ab"
                        background-color="#dfe3f3" />
                </div>

                <div class="sa-card">
                    <h2 class="sa-title">{{ page.title }}</h2>
                    <p v-if="page.subtitle" class="sa-muted mb-5">{{ page.subtitle }}</p>

                    <!-- About you -->
                    <template v-if="page.type === 'background'">
                        <div v-for="item in page.items" :key="item.id" class="sa-question" :ref="'q_' + item.id">
                            <div class="sa-question__text">{{ item.text }}</div>
                            <div class="sa-options">
                                <button v-for="opt in item.options" :key="opt.id" type="button" class="sa-option"
                                    :class="{ 'is-selected': responses[item.id] === opt.id }"
                                    @click="answer(item.id, opt.id)">
                                    <span class="sa-radio" />
                                    <span>{{ opt.text }}</span>
                                </button>
                            </div>
                            <div v-if="showErrors && !isAnswered(item.id)" class="sa-error">Please choose an option.</div>
                        </div>
                    </template>

                    <!-- Agree / disagree -->
                    <template v-else-if="page.type === 'likert'">
                        <div v-for="item in page.items" :key="item.id" class="sa-question sa-question--likert"
                            :ref="'q_' + item.id">
                            <div class="sa-question__text">{{ item.text }}</div>
                            <div class="sa-likert">
                                <button v-for="(label, value) in questions.likert_scale" :key="value" type="button"
                                    class="sa-likert__btn" :class="{ 'is-selected': responses[item.id] === Number(value) }"
                                    :aria-label="label" @click="answer(item.id, Number(value))">
                                    <span class="sa-likert__dot" />
                                    <span class="sa-likert__label">{{ label }}</span>
                                </button>
                            </div>
                            <div v-if="showErrors && !isAnswered(item.id)" class="sa-error">Please choose an answer.</div>
                        </div>
                    </template>

                    <!-- Pick the one most like you -->
                    <template v-else-if="page.type === 'forced'">
                        <div v-for="(item, index) in page.items" :key="item.id" class="sa-question" :ref="'q_' + item.id">
                            <div class="sa-question__text sa-question__text--small">Pair {{ page.offset + index + 1 }}</div>
                            <div class="sa-pair">
                                <button v-for="opt in item.options" :key="opt.id" type="button" class="sa-pair__card"
                                    :class="{ 'is-selected': responses[item.id] === opt.id }"
                                    @click="answer(item.id, opt.id)">
                                    <span class="sa-radio" />
                                    <span>{{ opt.text }}</span>
                                </button>
                            </div>
                            <div v-if="showErrors && !isAnswered(item.id)" class="sa-error">Please pick one.</div>
                        </div>
                    </template>

                    <!-- Scenarios -->
                    <template v-else-if="page.type === 'scenario'">
                        <div v-for="(item, index) in page.items" :key="item.id" class="sa-question" :ref="'q_' + item.id">
                            <div class="sa-scenario__label">Situation {{ page.offset + index + 1 }}</div>
                            <div class="sa-question__text">{{ item.text }}</div>
                            <div class="sa-options">
                                <button v-for="opt in item.options" :key="opt.id" type="button" class="sa-option"
                                    :class="{ 'is-selected': responses[item.id] === opt.id }"
                                    @click="answer(item.id, opt.id)">
                                    <span class="sa-radio" />
                                    <span>{{ opt.text }}</span>
                                </button>
                            </div>
                            <div v-if="showErrors && !isAnswered(item.id)" class="sa-error">Please choose what you'd do.</div>
                        </div>
                    </template>

                    <!-- Written -->
                    <template v-else-if="page.type === 'written'">
                        <div v-for="item in page.items" :key="item.id" class="sa-question sa-question--written"
                            :ref="'q_' + item.id">
                            <div class="sa-question__text sa-question__text--large">{{ item.text }}</div>
                            <p class="sa-muted mb-3">{{ item.hint }}</p>
                            <v-textarea :value="responses[item.id] || ''" outlined auto-grow rows="6" maxlength="4000"
                                placeholder="Write your answer here…" hide-details
                                @input="answer(item.id, $event)" @paste="onPaste(item.id)"
                                @keydown="onKey(item.id, $event)" />
                            <div class="sa-wordhint" :class="{ 'is-ok': wordCount(responses[item.id]) >= minWords }">
                                <v-icon x-small :color="wordCount(responses[item.id]) >= minWords ? '#2e7d32' : '#8a90a6'">
                                    {{ wordCount(responses[item.id]) >= minWords ? 'mdi-check-circle' : 'mdi-information-outline' }}
                                </v-icon>
                                {{ wordCount(responses[item.id]) }} words · aim for 50 to 200. Give one specific example: what
                                happened, what you did and how it turned out. Real examples matter more than polished writing.
                            </div>
                            <div v-if="showErrors && !isAnswered(item.id)" class="sa-error">
                                Please write at least {{ minWords }} words.
                            </div>
                        </div>
                    </template>

                    <v-alert v-if="submitError" type="error" dense text class="mt-2">{{ submitError }}</v-alert>

                    <div class="sa-nav">
                        <v-btn v-if="step > 0" text class="text-none" color="#3949ab" :disabled="submitting"
                            @click="back">
                            <v-icon left>mdi-arrow-left</v-icon> Back
                        </v-btn>
                        <v-spacer />
                        <v-btn v-if="!isLastPage" large depressed dark color="#3949ab" class="text-none sa-cta"
                            @click="next">
                            Continue <v-icon right>mdi-arrow-right</v-icon>
                        </v-btn>
                        <v-btn v-else large depressed dark color="#2e7d32" class="text-none sa-cta"
                            :loading="submitting" @click="submit">
                            Submit my answers <v-icon right>mdi-send</v-icon>
                        </v-btn>
                    </div>
                </div>

                <div class="sa-footer-note">
                    Not {{ name || 'you' }}?
                    <a href="#" @click.prevent="startOver">Start again</a>
                </div>
            </div>

            <!-- Done -->
            <div v-else-if="stage === 'done'" class="sa-card text-center">
                <div class="sa-done-icon">
                    <v-icon size="44" color="white">mdi-check</v-icon>
                </div>
                <h1 class="sa-hero mt-4">Thank you{{ firstName ? ', ' + firstName : '' }}!</h1>
                <p class="sa-lead">
                    Your questionnaire has been submitted. Our recruitment team reviews every application personally,
                    and we'll be in touch by email about the next steps.
                </p>
                <p class="sa-muted mb-0">You can close this page now.</p>
            </div>
        </main>
    </div>
</template>

<script>
const STORAGE_KEY = 'dg_sales_assessment'

// Small seeded PRNG, so a candidate's question and option order is random
// but stays the same when they resume.
function seededRandom(seedText) {
    let h = 1779033703 ^ seedText.length
    for (let i = 0; i < seedText.length; i++) {
        h = Math.imul(h ^ seedText.charCodeAt(i), 3432918353)
        h = (h << 13) | (h >>> 19)
    }
    return function () {
        h = Math.imul(h ^ (h >>> 16), 2246822507)
        h = Math.imul(h ^ (h >>> 13), 3266489909)
        h ^= h >>> 16
        return (h >>> 0) / 4294967296
    }
}

function shuffle(list, random) {
    const copy = list.slice()
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]]
    }
    return copy
}

function chunk(list, sizes) {
    const out = []
    let index = 0
    sizes.forEach(size => {
        if (index < list.length) out.push(list.slice(index, index + size))
        index += size
    })
    if (index < list.length) out.push(list.slice(index))
    return out
}

export default {
    name: 'SalesAssessmentComponent',
    data() {
        return {
            stage: 'loading',
            loadError: '',
            questions: null,

            name: '',
            email: '',
            consent: false,
            consentError: false,
            formValid: false,
            starting: false,
            startError: '',

            token: null,
            responses: {},
            pastes: {},
            // Keystrokes per written answer: text that appears without typing
            // (an AI agent filling the form) is a signal for reviewers.
            keys: {},
            step: 0,
            showErrors: false,
            saving: false,
            submitting: false,
            submitError: '',

            rules: {
                required: v => !!(v && String(v).trim()) || 'Required',
                email: v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '') || 'Please enter a valid email',
            },
        }
    },
    computed: {
        minWords() {
            return this.questions ? this.questions.min_words : 40
        },
        firstName() {
            return (this.name || '').split(' ')[0]
        },
        pages() {
            if (!this.questions || !this.token) return []
            const q = this.questions
            const random = seededRandom(this.token)
            const pages = []

            pages.push({
                type: 'background', sectionLabel: 'About you', title: 'A little about you',
                subtitle: "These help us plan, and don't affect your score.", items: q.background,
            })

            const statements = chunk(shuffle(q.likert, random), [10, 10, 10])
            statements.forEach((items, i) => pages.push({
                type: 'likert', sectionLabel: 'How you work',
                title: i === 0 ? 'How much do you agree?' : 'How much do you agree? (continued)',
                subtitle: i === 0 ? "Go with your first instinct. There are no right or wrong answers." : '',
                items,
            }))

            const forced = shuffle(q.forced, random).map(item => ({ ...item, options: shuffle(item.options, random) }))
            let offset = 0
            chunk(forced, [5, 5]).forEach((items, i) => {
                pages.push({
                    type: 'forced', sectionLabel: 'Most like you', offset,
                    title: 'Which sounds more like you?',
                    subtitle: i === 0 ? 'Both options are fine. Pick the one that fits you better, even if only slightly.' : '',
                    items,
                })
                offset += items.length
            })

            const scenarios = q.scenarios.map(item => ({ ...item, options: shuffle(item.options, random) }))
            offset = 0
            chunk(scenarios, [4, 4, 3]).forEach((items, i) => {
                pages.push({
                    type: 'scenario', sectionLabel: 'What would you do?', offset,
                    title: 'What would you do?',
                    subtitle: i === 0 ? "Some of these are set in a customer-facing job. You don't need any experience: there's no single right answer, so pick what you'd actually do." : '',
                    items,
                })
                offset += items.length
            })

            q.written.forEach((item, i) => pages.push({
                type: 'written', sectionLabel: 'In your own words',
                title: `In your own words (${i + 1} of ${q.written.length})`,
                subtitle: i === 0 ? "We care about what you did and how you think, not perfect spelling or grammar." : '',
                items: [item],
            }))

            return pages
        },
        page() {
            return this.pages[this.step] || null
        },
        isLastPage() {
            return this.step === this.pages.length - 1
        },
        progress() {
            return this.pages.length ? Math.round((this.step / this.pages.length) * 100) : 0
        },
    },
    created() {
        this.init()
    },
    methods: {
        async init() {
            this.stage = 'loading'
            const res = await this.$axios.get('api/sales-assessment/questions')
            if (!res.data || !res.data.response) {
                this.loadError = (res.data && res.data.message) || 'Please check your connection and try again.'
                this.stage = 'error'
                return
            }
            this.questions = res.data.questions

            const saved = this.readLocal()
            if (saved && saved.token) {
                const show = await this.$axios.get(`api/sales-assessment/${saved.token}`)
                if (show.data && show.data.response) {
                    this.token = saved.token
                    this.name = show.data.name
                    if (show.data.submitted) {
                        this.stage = 'done'
                        return
                    }
                    // Local answers are the newest; server fills any gaps.
                    this.responses = { ...show.data.responses, ...(saved.responses || {}) }
                    this.pastes = saved.pastes || {}
                    this.keys = saved.keys || {}
                    this.step = Math.min(Math.max(saved.step || 0, show.data.current_step || 0), this.pages.length - 1)
                    this.stage = 'assessment'
                    return
                }
                if (show.status === 404) {
                    this.clearLocal()
                }
            }
            this.stage = 'welcome'
        },

        async start() {
            const valid = this.$refs.form.validate()
            this.consentError = !this.consent
            if (!valid || !this.consent) return
            this.starting = true
            this.startError = ''
            const res = await this.$axios.post('api/sales-assessment/start', {
                name: this.name,
                email: this.email,
                consent: this.consent,
                source: this.$route.query.source || null,
            })
            this.starting = false
            if (res.status === 429) {
                this.startError = 'Too many attempts from this connection. Please wait a minute and try again.'
                return
            }
            if (!res.data || !res.data.response) {
                this.startError = (res.data && res.data.message) || 'Something went wrong. Please try again.'
                return
            }
            this.token = res.data.token
            this.responses = {}
            this.pastes = {}
            this.keys = {}
            this.step = 0
            this.writeLocal()
            this.stage = 'assessment'
            window.scrollTo({ top: 0 })
        },

        answer(id, value) {
            this.$set(this.responses, id, value)
            this.writeLocal()
        },

        onPaste(id) {
            this.$set(this.pastes, id, (this.pastes[id] || 0) + 1)
        },

        onKey(id, event) {
            if (event.key && (event.key.length === 1 || ['Enter', 'Backspace', 'Unidentified', 'Process'].includes(event.key))) {
                this.$set(this.keys, id, (this.keys[id] || 0) + 1)
            }
        },

        wordCount(text) {
            return (text || '').trim().split(/\s+/).filter(Boolean).length
        },

        isAnswered(id) {
            const value = this.responses[id]
            if (this.questions.written.some(w => w.id === id)) {
                return this.wordCount(value) >= this.minWords
            }
            return value !== undefined && value !== null && value !== ''
        },

        firstUnanswered(pageItems) {
            return pageItems.find(item => !this.isAnswered(item.id))
        },

        scrollToQuestion(id) {
            this.$nextTick(() => {
                const ref = this.$refs['q_' + id]
                const el = Array.isArray(ref) ? ref[0] : ref
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            })
        },

        next() {
            const missing = this.firstUnanswered(this.page.items)
            if (missing) {
                this.showErrors = true
                this.scrollToQuestion(missing.id)
                return
            }
            this.showErrors = false
            this.step++
            this.writeLocal()
            this.saveProgress()
            window.scrollTo({ top: 0, behavior: 'smooth' })
        },

        back() {
            this.showErrors = false
            this.step = Math.max(0, this.step - 1)
            this.writeLocal()
            window.scrollTo({ top: 0, behavior: 'smooth' })
        },

        // Best-effort: answers also live in localStorage, and submit sends them all.
        async saveProgress() {
            this.saving = true
            await this.$axios.post(`api/sales-assessment/${this.token}/progress`, {
                responses: this.responses,
                current_step: this.step,
                meta: { pastes: this.pastes, keys: this.keys },
            })
            this.saving = false
        },

        async submit() {
            const missing = this.firstUnanswered(this.page.items)
            if (missing) {
                this.showErrors = true
                this.scrollToQuestion(missing.id)
                return
            }
            this.submitting = true
            this.submitError = ''
            const res = await this.$axios.post(`api/sales-assessment/${this.token}/submit`, {
                responses: this.responses,
                meta: { pastes: this.pastes, keys: this.keys },
            })
            this.submitting = false

            if (res.data && res.data.response) {
                this.writeLocal({ submitted: true })
                this.stage = 'done'
                window.scrollTo({ top: 0 })
                return
            }

            // Jump to whichever question the server says is incomplete.
            const questionId = res.data && res.data.question_id
            if (questionId) {
                const index = this.pages.findIndex(p => p.items.some(item => item.id === questionId))
                if (index >= 0) {
                    this.step = index
                    this.showErrors = true
                    this.scrollToQuestion(questionId)
                }
            }
            this.submitError = (res.data && res.data.message) || 'We could not submit your answers. Please try again.'
        },

        startOver() {
            if (!confirm('Start again? Answers on this device will be cleared.')) return
            this.clearLocal()
            this.token = null
            this.responses = {}
            this.pastes = {}
            this.keys = {}
            this.step = 0
            this.name = ''
            this.email = ''
            this.consent = false
            this.stage = 'welcome'
        },

        readLocal() {
            try {
                return JSON.parse(localStorage.getItem(STORAGE_KEY))
            } catch (e) {
                return null
            }
        },

        writeLocal(extra = {}) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({
                    token: this.token,
                    responses: this.responses,
                    pastes: this.pastes,
                    keys: this.keys,
                    step: this.step,
                    ...extra,
                }))
            } catch (e) {
                // Private mode or full storage: the server copy still works.
            }
        },

        clearLocal() {
            localStorage.removeItem(STORAGE_KEY)
        },
    },
}
</script>

<style scoped>
.sa-page {
    min-height: 100vh;
    background: #f4f6fb;
    color: #1f2433;
    font-size: 16px;
}

.sa-header {
    background: linear-gradient(120deg, #1a237e 0%, #3949ab 60%, #5c6bc0 100%);
    padding: 18px 20px 64px;
}

.sa-header__inner {
    max-width: 760px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.sa-brand {
    color: #fff;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-size: 14px;
}

.sa-header__save {
    color: rgba(255, 255, 255, 0.85);
    font-size: 12px;
}

.sa-main {
    max-width: 760px;
    margin: -44px auto 0;
    padding: 0 16px 48px;
}

.sa-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(26, 35, 126, 0.08), 0 1px 3px rgba(26, 35, 126, 0.06);
    padding: 32px;
}

.sa-eyebrow {
    color: #3949ab;
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.sa-hero {
    font-size: 30px;
    line-height: 1.2;
    font-weight: 700;
    margin: 6px 0 12px;
}

.sa-title {
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 6px;
}

.sa-lead {
    font-size: 17px;
    line-height: 1.55;
    color: #3b4157;
}

.sa-muted {
    color: #646b82;
    line-height: 1.5;
}

.sa-points {
    display: grid;
    gap: 14px;
    margin: 22px 0 26px;
    padding: 18px;
    border-radius: 12px;
    background: #f1f3fb;
}

.sa-point {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    line-height: 1.45;
    color: #3b4157;
}

.sa-consent {
    border: 1px solid #e1e4ef;
    border-radius: 12px;
    padding: 14px 16px;
}

.sa-consent--error {
    border-color: #c62828;
    background: #fdf3f3;
}

.sa-consent__error {
    margin-left: 32px;
}

.sa-consent__label {
    color: #1f2433;
    font-weight: 600;
    font-size: 15px;
}

.sa-consent__text {
    margin: 8px 0 0 32px;
    font-size: 13px;
    line-height: 1.5;
    color: #646b82;
}

.sa-cta {
    border-radius: 10px !important;
    letter-spacing: 0;
    font-weight: 600;
}

.sa-progress {
    background: #fff;
    border-radius: 14px;
    padding: 14px 18px;
    margin-bottom: 14px;
    box-shadow: 0 4px 14px rgba(26, 35, 126, 0.06);
}

.sa-progress__row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 13px;
}

.sa-progress__section {
    font-weight: 700;
    color: #3949ab;
}

.sa-progress__count {
    color: #646b82;
}

.sa-question {
    padding: 20px 0;
    border-top: 1px solid #eceef5;
}

.sa-question:first-of-type {
    border-top: none;
    padding-top: 4px;
}

.sa-question__text {
    font-size: 17px;
    font-weight: 600;
    line-height: 1.45;
    margin-bottom: 14px;
}

.sa-question__text--small {
    font-size: 12px;
    font-weight: 700;
    color: #8a90a6;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 10px;
}

.sa-question__text--large {
    font-size: 19px;
    margin-bottom: 6px;
}

.sa-scenario__label {
    font-size: 12px;
    font-weight: 700;
    color: #3949ab;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 6px;
}

.sa-options {
    display: grid;
    gap: 10px;
}

.sa-option,
.sa-pair__card {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: left;
    padding: 14px 16px;
    border: 1.5px solid #dde1ee;
    border-radius: 12px;
    background: #fff;
    font-size: 15px;
    line-height: 1.4;
    color: #1f2433;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.sa-option:hover,
.sa-pair__card:hover {
    border-color: #9fa8da;
}

.sa-option.is-selected,
.sa-pair__card.is-selected {
    border-color: #3949ab;
    background: #eef0fb;
    box-shadow: 0 0 0 3px rgba(57, 73, 171, 0.12);
}

.sa-radio {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid #b7bdd3;
    position: relative;
}

.is-selected .sa-radio {
    border-color: #3949ab;
}

.is-selected .sa-radio::after {
    content: '';
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: #3949ab;
}

.sa-pair {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.sa-pair__card {
    align-items: flex-start;
    min-height: 84px;
}

.sa-likert {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
}

.sa-likert__btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 10px 4px;
    border: 1.5px solid #dde1ee;
    border-radius: 12px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
}

.sa-likert__btn:hover {
    border-color: #9fa8da;
}

.sa-likert__dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #b7bdd3;
}

.sa-likert__label {
    font-size: 12px;
    line-height: 1.2;
    color: #4a5068;
    text-align: center;
}

.sa-likert__btn.is-selected {
    border-color: #3949ab;
    background: #eef0fb;
}

.sa-likert__btn.is-selected .sa-likert__dot {
    border-color: #3949ab;
    background: #3949ab;
    box-shadow: inset 0 0 0 3px #fff;
}

.sa-likert__btn.is-selected .sa-likert__label {
    color: #1a237e;
    font-weight: 600;
}

.sa-wordhint {
    font-size: 13px;
    color: #8a90a6;
    margin-top: 8px;
}

.sa-wordhint.is-ok {
    color: #2e7d32;
}

.sa-error {
    color: #c62828;
    font-size: 13px;
    margin-top: 8px;
    font-weight: 500;
}

.sa-nav {
    display: flex;
    align-items: center;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid #eceef5;
}

.sa-footer-note {
    text-align: center;
    font-size: 13px;
    color: #8a90a6;
    margin-top: 18px;
}

.sa-footer-note a {
    color: #3949ab;
}

.sa-done-icon {
    width: 76px;
    height: 76px;
    margin: 4px auto 0;
    border-radius: 50%;
    background: linear-gradient(135deg, #2e7d32, #43a047);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 20px rgba(46, 125, 50, 0.25);
}

@media (max-width: 600px) {
    .sa-card {
        padding: 22px 18px;
        border-radius: 14px;
    }

    .sa-hero {
        font-size: 25px;
    }

    .sa-pair {
        grid-template-columns: 1fr;
    }

    .sa-pair__card {
        min-height: 0;
    }

    .sa-likert {
        gap: 4px;
    }

    .sa-likert__btn {
        padding: 8px 2px;
    }

    .sa-likert__label {
        font-size: 10.5px;
    }

    .sa-consent__text {
        margin-left: 0;
    }
}
</style>
